import { z } from "zod";

import { decisionPreviewGapSchema } from "./bank-decision-preview.js";
import { OWN_FILE_ROUTES } from "./bank-own-file.js";

/**
 * `DECISION-DOOR-CONTRACT` (`P1-LND-076`): **`GET /api/v1/bank/decisions/{id}`'s answer, one contract for the door and for every client** (A48: a stub speaks the provider's contract, never the
 * client's expectation). The door answered `Promise<unknown>` while it grew `axis` (`CHEF-AXIS`), `committeeReasons`, the extra `controls[]` row and `gapCount` (`COMMITTEE-REASONS`,
 * `GAPS-WITHOUT-CONTROL`); the chef's screen bound them from the code. An integration spec parses the door's REAL answer with {@link bankDecisionAnswerSchema}, so a field added on one side and not
 * the other fails there. The door does NOT validate at runtime: a read door the chef uses must never turn an unforeseen field into an error page.
 *
 * Strict on purpose, as `notifications-answer.ts` is: an extra key is a mismatch, not a tolerated surplus. One scope line: `underwriting.grade`, `.opinion` and `.run` are the bank's PROJECTION of the
 * intelligence view (`bank-views.ts`), shared with `GET /bank/files/{id}/underwriting`; each is held here to be an object (or `null`) and its inner shape is that view's contract, booked as its own item
 * (`BANK-UNDERWRITING-VIEW-CONTRACT`), not repeated from the code in this one.
 */
/**
 * `DECISION-ANSWER-LENIENT`: **one definition, two readings.** The shapes are written ONCE, in {@link buildSchemas}, with the object constructor as its one parameter: `z.strictObject` gives the
 * contract (stubs, the OpenAPI parity spec: an extra key is a mismatch), `z.looseObject` gives the READ twin the bank client parses the live answer with (extra keys tolerated at EVERY level,
 * every required field and type identical, a missing required field still fails loudly, A47). No field list is copied: a field added below is in both. The loose object differs from the strict one only
 * in its unknown-keys policy, so the same inferred type is exposed for both (the extra keys a tolerant read lets through are not typed).
 */
function buildSchemas(object: typeof z.strictObject) {
  const wholeAmount = z.number().int();
  const text = z.string();

  /** The rate control's policy: the grid's rate for the letter, the tolerance, the letter (`decision-controls.ts`). */
  const policyValues = z.record(z.string(), z.union([z.number(), z.string()]));

  /** One control row: `proposed` is the offer's own value, `policy` what it is held against, `gapPts` only on the rate row in `ecart`. */
  const decisionControlSchema = object({
    code: z.enum(["rate", "duration", "taeg", "capacity", "ltv"]),
    proposed: z.union([z.number(), z.string()]),
    policy: policyValues,
    state: z.enum(["ok", "ecart", "bloquant"]),
    gapPts: z.number().optional(),
  });

  /** `GAPS-WITHOUT-CONTROL`: the gap no control row carries, D2's words (frame 170, `chef.js:45`), drawn after « Montant ». */
  const decisionGapControlSchema = object({
    code: z.literal("vehicleAgeAtMaturity"),
    label: text,
    proposed: text,
    limit: text,
    state: z.literal("ecart"),
    level: z.enum(["comite", "chef"]),
  });

  /** `CHEF-AXIS`: the axis' window, ticks, tolerance zone and markers, positions and D2's words served. */
  const decisionAxisSchema = object({
    min: text,
    max: text,
    ticks: z.tuple([text, text, text]),
    zone: object({ startPos: z.number(), endPos: z.number() }),
    markers: z.array(
      object({
        kind: z.enum(["grid", "proposed", "gridAndProposed"]),
        label: text,
        posPct: z.number(),
        beyond: z.enum(["left", "right"]).nullable(),
      }),
    ),
  });

  /** `COMMITTEE-REASONS`: why the committee, by code, in D2's words (`chef.js:44`). */
  const committeeReasonWordsSchema = object({ code: text, words: text });

  /** The reason as the acceptance kept it (`committeeReasonsOf`). */
  const keptCommitteeReasonSchema = z.discriminatedUnion("kind", [
    object({ kind: z.literal("amount"), amountFinanced: wholeAmount, thresholdAmount: wholeAmount }),
    object({
      kind: z.literal("rule"),
      code: z.enum([
        "rateBelowGrid",
        "gridRateMissing",
        "durationAboveMax",
        "vehicleAgeAtMaturity",
        "ltvAboveMax",
      ]),
      rule: text,
      detail: text,
    }),
  ]);

  const committeeSittingSchema = object({
    sequence: z.number().int(),
    sittingDate: text,
    outcome: z.enum(["accorde", "accordeSousConditions", "refuse", "ajourne"]),
    summary: text,
    conditions: z.array(text),
    recordedBy: text,
    recordedOn: text,
  });

  const decisionRecordSchema = object({
    id: text,
    fileId: text,
    offerId: text,
    reference: text,
    clientName: text.nullable(),
    outcome: z.enum(["validee", "renvoyee", "refusee"]),
    ratePct: text.nullable(),
    rateAdjusted: z.boolean(),
    note: text.nullable(),
    decidedByName: text.nullable(),
    decidedAt: text,
    submittedAt: text.nullable(),
    instructions: z.array(object({ id: text, position: z.number().int(), text, treatedAt: text.nullable() })),
    // `CHEF-SELF-VALIDATION` R1: « Instruit et validé par le chef du crédit » on a decision the chef took on the file she holds (`null` otherwise).
    journalFact: text.nullable(),
  });

  const recapSchema = object({
    totalFinancedAmount: wholeAmount,
    instalmentAmount: wholeAmount,
    insuranceInstalmentAmount: wholeAmount,
    capacityAmount: wholeAmount.nullable(),
    capacityAtSubmissionAmount: wholeAmount,
    capacityBasis: z.enum(["run", "soumission"]),
    capacityUsedPct: z.number(),
    hakiliFeeAmount: wholeAmount,
    bankFeeAmount: wholeAmount,
    warrantyAmount: wholeAmount,
    taegPct: text,
    underCeiling: z.boolean(),
    ltvVehiclePct: z.number().nullable(),
    ltvTotalPct: z.number().nullable(),
    gridRatePct: text.nullable(),
    gridDeltaPts: z.number().nullable(),
    gridDeltaGrade: text.nullable(),
    feeImpactLabel: text,
  });

  const bankDecisionAnswerSchema = object({
    // The queue row (`QueueRow`).
    id: text,
    offerId: text,
    reference: text,
    clientName: text.nullable(),
    analystName: text.nullable(),
    amountFinanced: wholeAmount,
    durationMonths: z.number().int(),
    nominalRatePct: text,
    instalmentAmount: wholeAmount,
    taegPct: text,
    gridRatePct: text.nullable(),
    gaps: z.array(object(decisionPreviewGapSchema.shape)),
    requiredLevel: z.enum(["chefCredit", "comiteCredit"]).nullable(),
    justification: text,
    submittedAt: text.nullable(),
    waitHours: z.number().nullable(),
    fresh: z.boolean(),
    late: z.boolean(),
    delegationThresholdAmount: wholeAmount.nullable(),
    requiresCommittee: z.boolean(),
    stage: text,
    validatedAt: text.nullable(),
    transmittedAt: text.nullable(),
    validUntil: text.nullable(),
    // The decision screen's own reads.
    committee: object({
      reasons: z.array(keptCommitteeReasonSchema),
      sittings: z.array(committeeSittingSchema),
    }).nullable(),
    underwriting: object({
      grade: z.record(z.string(), z.unknown()).nullable(),
      gridMissingGrades: z.array(text),
      opinion: z.record(z.string(), z.unknown()).nullable(),
      run: z.record(z.string(), z.unknown()),
    }).nullable(),
    facts: object({
      depositPct: z.number().nullable(),
      expertisedValueAmount: wholeAmount.nullable(),
      hakiliFeeAmount: wholeAmount,
      bankFeeAmount: wholeAmount,
      capacityAmount: wholeAmount,
      borrowerInsuranceRatePct: z.number(),
    }),
    recap: recapSchema,
    controls: z.array(z.union([decisionControlSchema, decisionGapControlSchema])),
    gapCount: z.number().int().min(0),
    committeeReasons: z.array(committeeReasonWordsSchema),
    axis: decisionAxisSchema.nullable(),
    rateTolerancePct: text.nullable(),
    vehicleLabel: text.nullable(),
    gridComparable: z.boolean(),
    gradePending: z.boolean(),
    gradeNote: text.nullable(),
    // `CHEF-SELF-VALIDATION` R1: the chef who instructed this file herself, the route and the sentence the server serves (`null` otherwise).
    ownFile: object({ route: z.enum(OWN_FILE_ROUTES), sentence: text.nullable(), notice: text }).nullable(),
    decisions: z.array(decisionRecordSchema),
    refusalReasons: z.array(object({ code: text, label: text })),
    // `CHEF-ASK-RIGHT` (`P1-LND-087`): whether the chef may ask Hakili, served; the notice is the server's sentence, `null` while the entry is open.
    askHakili: object({ open: z.boolean(), notice: text.nullable() }),
  });

  return {
    decisionControlSchema,
    decisionGapControlSchema,
    decisionAxisSchema,
    committeeReasonWordsSchema,
    bankDecisionAnswerSchema,
  };
}

const strict = buildSchemas(z.strictObject);
// The loose constructor is the strict one's twin in everything but the unknown-keys policy, so the two share one inferred type.
const loose = buildSchemas(z.looseObject);

export const decisionControlSchema = strict.decisionControlSchema;
export const decisionGapControlSchema = strict.decisionGapControlSchema;
export const decisionAxisSchema = strict.decisionAxisSchema;
export const committeeReasonWordsSchema = strict.committeeReasonWordsSchema;
export const bankDecisionAnswerSchema = strict.bankDecisionAnswerSchema;

/**
 * The READ twin (`DECISION-ANSWER-LENIENT`, A47): what the bank client parses the live answer with. Extra keys pass at the top level and inside every nested object (the controls, the gap rows, the axis and
 * its markers, the recap, the facts, the committee and its sittings, the decisions and their instructions, the committee reasons, the refusal reasons); a missing required field or a wrong type fails exactly as
 * in {@link bankDecisionAnswerSchema}. The strict schema stays for stubs and the parity spec.
 */
export const bankDecisionAnswerReadSchema = loose.bankDecisionAnswerSchema;

export type BankDecisionAnswer = z.infer<typeof bankDecisionAnswerSchema>;
export type DecisionControl = z.infer<typeof decisionControlSchema>;
export type DecisionGapControl = z.infer<typeof decisionGapControlSchema>;
export type DecisionAxis = z.infer<typeof decisionAxisSchema>;
